(() => {
  if (window.cntrHomeLoaded) return;
  window.cntrHomeLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Hero: mobile crossfade slideshow + header state for the transparent mobile header
     ------------------------------------------------------------------ */
  const initHero = (hero) => {
    if (hero.dataset.ready) return;
    hero.dataset.ready = 'true';

    const slides = Array.from(hero.querySelectorAll('[data-hero-slide]'));
    const interval = Number(hero.dataset.interval || 3000);
    let index = 0;
    let timer = null;

    const show = (next) => {
      slides[index].classList.remove('is-active');
      index = (next + slides.length) % slides.length;
      slides[index].classList.add('is-active');
    };

    const start = () => {
      if (reduceMotion || slides.length < 2 || timer) return;
      timer = window.setInterval(() => show(index + 1), interval);
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    start();

    const onScroll = () => {
      document.body.classList.toggle('scrolled-past-100', window.scrollY > 100);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* ------------------------------------------------------------------
     Rails: arrows + progress bar
     ------------------------------------------------------------------ */
  const initRail = (root) => {
    const rail = root.querySelector('[data-rail]');
    if (!rail || rail.dataset.ready) return;
    rail.dataset.ready = 'true';

    const prev = root.querySelector('[data-rail-prev]');
    const next = root.querySelector('[data-rail-next]');
    const bar = root.querySelector('[data-rail-progress]');

    const step = () => {
      const item = rail.firstElementChild;
      if (!item) return rail.clientWidth;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      const perView = Math.max(1, Math.floor((rail.clientWidth + gap) / (item.offsetWidth + gap)));
      return perView * (item.offsetWidth + gap);
    };

    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max - 2;
      if (bar) {
        const visible = rail.scrollWidth ? rail.clientWidth / rail.scrollWidth : 1;
        const progress = max > 0 ? rail.scrollLeft / max : 0;
        bar.style.width = `${Math.min(100, visible * 100)}%`;
        bar.style.transform = `translateX(${progress * (1 / Math.max(visible, 0.0001) - 1) * 100}%)`;
      }
      root.toggleAttribute('data-rail-static', max <= 2);
    };

    prev?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
    root._hhRailUpdate = update;
  };

  /* ------------------------------------------------------------------
     Tabs
     ------------------------------------------------------------------ */
  const initTabs = (root) => {
    if (root.dataset.tabsReady) return;
    root.dataset.tabsReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));

    const select = (tab) => {
      tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected));
        t.tabIndex = selected ? 0 : -1;
      });
      root.querySelectorAll('[data-tab-link]').forEach((link) => {
        link.hidden = link.dataset.tabLink !== tab.getAttribute('aria-controls');
      });
      panels.forEach((panel) => {
        const active = panel.id === tab.getAttribute('aria-controls');
        panel.hidden = !active;
        if (active) {
          initRail(panel);
          panel._hhRailUpdate?.();
        }
      });
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const next = tabs[(i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
        next.focus();
        select(next);
      });
    });
  };

  /* ------------------------------------------------------------------
     Accordion: animate <details> open/close
     ------------------------------------------------------------------ */
  const initAccordion = (details) => {
    if (details.dataset.ready) return;
    details.dataset.ready = 'true';

    const summary = details.querySelector('summary');
    const content = details.querySelector('.hh-faq__answer');
    if (!summary || !content) return;
    let animation = null;

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      const opening = !details.classList.contains('is-open');
      // A closed <details> can still report its content height, so start from 0 unless mid-animation.
      const from = animation || !opening ? content.offsetHeight : 0;

      animation?.cancel();
      details.classList.toggle('is-open', opening);
      if (opening) details.open = true;

      if (reduceMotion) {
        details.open = opening;
        return;
      }

      const to = opening ? content.scrollHeight : 0;
      animation = content.animate(
        [
          { height: `${from}px`, opacity: opening ? 0 : 1 },
          { height: `${to}px`, opacity: opening ? 1 : 0 },
        ],
        { duration: opening ? 380 : 300, easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)' }
      );
      animation.onfinish = () => {
        animation = null;
        if (!opening) details.open = false;
      };
    });
  };

  /* ------------------------------------------------------------------
     Product cards: colour swatches switch the image + link. Cards also
     auto-cycle through their colours while on screen.
     ------------------------------------------------------------------ */
  const CYCLE_MS = 2500;

  const swapImage = (img, src, srcset) => {
    if (!img || !src) return;
    img.style.opacity = '0';
    window.setTimeout(() => {
      const show = () => {
        img.style.opacity = '';
      };
      img.addEventListener('load', show, { once: true });
      img.srcset = srcset || '';
      img.src = src;
      if (img.complete) show();
    }, 180);
  };

  const preload = (swatch) => {
    if (!swatch?.dataset.src || swatch.dataset.preloaded) return;
    swatch.dataset.preloaded = 'true';
    const image = new Image();
    image.sizes = '(min-width: 1100px) 25vw, (min-width: 750px) 30vw, 45vw';
    image.srcset = swatch.dataset.srcset || '';
    image.src = swatch.dataset.src;
  };

  const selectSwatch = (swatch) => {
    const card = swatch.closest('[data-hh-card]');
    if (!card || swatch.classList.contains('is-active')) return;

    card.querySelectorAll('[data-hh-swatch]').forEach((s) => s.classList.toggle('is-active', s === swatch));
    card.dataset.activeId = swatch.dataset.id;

    swapImage(card.querySelector('[data-hh-card-img]'), swatch.dataset.src, swatch.dataset.srcset);

    const img2 = card.querySelector('[data-hh-card-img2]');
    if (img2) {
      if (swatch.dataset.src2) {
        img2.srcset = swatch.dataset.srcset2 || '';
        img2.src = swatch.dataset.src2;
        img2.style.display = '';
      } else {
        img2.style.display = 'none';
      }
    }

    const link = card.querySelector('[data-hh-card-link]');
    if (link && swatch.dataset.url) link.href = swatch.dataset.url;
  };

  // One shared clock so every visible card changes colour at the same moment.
  const cyclingCards = new Set();
  let cycleTimer = null;

  const advance = (card) => {
    const swatches = Array.from(card.querySelectorAll('[data-hh-swatch]'));
    const index = swatches.findIndex((s) => s.classList.contains('is-active'));
    selectSwatch(swatches[(index + 1) % swatches.length]);
    preload(swatches[(index + 2) % swatches.length]);
  };

  const tick = () => {
    cyclingCards.forEach((card) => {
      if (!card._hhPaused) advance(card);
    });
  };

  const stopCycle = (card) => {
    cyclingCards.delete(card);
    if (!cyclingCards.size) {
      window.clearInterval(cycleTimer);
      cycleTimer = null;
    }
  };

  const startCycle = (card) => {
    if (reduceMotion || card._hhManual) return;
    const swatches = Array.from(card.querySelectorAll('[data-hh-swatch]'));
    if (swatches.length < 2) return;

    const index = swatches.findIndex((s) => s.classList.contains('is-active'));
    preload(swatches[(index + 1) % swatches.length]);
    cyclingCards.add(card);
    if (!cycleTimer) cycleTimer = window.setInterval(tick, CYCLE_MS);
  };

  const cycleObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) startCycle(entry.target);
              else stopCycle(entry.target);
            });
          },
          { threshold: 0.5 }
        )
      : null;

  const initCard = (card) => {
    if (card.dataset.ready) return;
    card.dataset.ready = 'true';
    card.querySelectorAll('[data-hh-swatch]').forEach((s) => {
      s.classList.toggle('is-active', s.dataset.id === card.dataset.activeId);
    });

    card.addEventListener('mouseenter', () => {
      card._hhPaused = true;
    });
    card.addEventListener('mouseleave', () => {
      card._hhPaused = false;
    });
    cycleObserver?.observe(card);
  };

  // The shopper choosing a colour takes over from the auto-cycle for that card.
  const chooseSwatch = (swatch) => {
    const card = swatch.closest('[data-hh-card]');
    if (card) {
      card._hhManual = true;
      stopCycle(card);
    }
    selectSwatch(swatch);
  };

  const canHover = window.matchMedia('(hover: hover)').matches;
  document.addEventListener(
    'pointerover',
    (event) => {
      if (!canHover) return;
      const swatch = event.target.closest?.('[data-hh-swatch]');
      if (swatch) chooseSwatch(swatch);
    },
    { passive: true }
  );
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.('[data-hh-swatch]');
    if (!swatch) return;
    event.preventDefault();
    chooseSwatch(swatch);
  });

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px' }
        )
      : null;

  const init = (scope = document) => {
    scope.querySelectorAll('[data-hh-hero]').forEach(initHero);
    scope.querySelectorAll('[data-hh-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-hh-accordion]').forEach(initAccordion);
    scope.querySelectorAll('[data-hh-card]').forEach(initCard);
    scope.querySelectorAll('[data-hh-rail-root]').forEach((root) => {
      if (!root.closest('[role="tabpanel"][hidden]')) initRail(root);
    });
    scope.querySelectorAll('.hh-reveal:not(.is-in)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
